/**
 * CanvasVideoSlowPath — shared pre-flight dialog for browsers that lack
 * the WebCodecs MP4 path. Without this gate, MP4 export on Yandex Browser,
 * older Firefox and some in-app webviews falls through to a 5–15 minute
 * MediaRecorder+FFmpeg-WASM transcode that users abandon mid-flight,
 * believing the tool is stuck (slideshow-maker session 3abf45d3 / Rita
 * 2026-05-24 — telemetry shows 17 % of MP4 exports hit this path and
 * 30 % of all exports never reach `export_complete`).
 *
 * Two tools (slideshow-maker + audio-to-video) share the same render
 * pipeline (CanvasVideoExport ↔ A2VWebCodecsExport alias) so they share
 * the same fallback shape and the same UX response: confirm with the
 * user before committing to a long encode and offer the WebM exit
 * which is fast on every browser that has MediaRecorder.
 *
 * API:
 *   const { estimateSeconds, recordActual, show } = window.CanvasVideoSlowPath;
 *
 *   const est = estimateSeconds({ durationSec, deviceMemory });
 *   //   → { minSec, maxSec, recordSec }            (legacy shape, unchanged)
 *   const est = estimateSeconds({ durationSec, lane: 'a2v-ff', fastLane: 'a2v-wc' });
 *   //   → { minSec, maxSec, midSec, fastSec, recordSec, measured }
 *   recordActual({ lane: 'a2v-ff', outSec, wallMs, hiddenMs });
 *
 *   const choice = await show({
 *     strings: {
 *       title, body, useWebm, keepMp4, cancel,
 *       // body MAY contain {min} / {max} / {fast} placeholders, filled
 *       // from `est` in minutes.
 *       hint,                     // optional second paragraph (e.g. the
 *                                 //   "your browser lost its H.264 codec"
 *                                 //   explanation) — plain text
 *     },
 *     est,                        // result of estimateSeconds
 *     allowWebm: true,            // default true — set false when the WebM
 *                                 // exit is NOT actually faster for this
 *                                 // caller (a2v has no MediaRecorder lane, so
 *                                 // on a browser without WebCodecs its WebM
 *                                 // takes the same slow FFmpeg path)
 *     allowKeepMp4: true,         // default true — set false to hide the
 *                                 //   slow option entirely (rare)
 *   });
 *   //   → 'webm' | 'mp4' | 'cancel'
 *
 * Callers also wire telemetry around the call (slow_path_dialog_shown,
 * slow_path_chose_{webm,mp4}, slow_path_cancelled). The shared CSS lives
 * in app.css under `.cvslow-*`.
 */
(function () {
    'use strict';

    /* ── measured per-device export rate ──────────────────────────────────
     * The honest estimate is the one THIS device produced last time (same
     * pattern as music-video-maker's est_time). Keyed by lane; value is an
     * EMA of ms per output-second. Population priors below only cover the
     * first-ever run. localStorage (not __toolSettings) because the rate is
     * a device property shared by every tool on this pipeline. */
    const RATE_KEY = 'cvx_rates_v1';
    function _rates() {
        try { return JSON.parse(localStorage.getItem(RATE_KEY) || '{}') || {}; }
        catch (_) { return {}; }
    }
    function recordActual(o) {
        o = o || {};
        if (!o.lane || !o.outSec || !o.wallMs || o.outSec < 5) return;
        // A background-throttled run measures the throttle, not the device:
        // field p90 is 13.7× duration exactly because of hidden tabs. Skip.
        if (o.hiddenMs && o.hiddenMs > 0.2 * o.wallMs) return;
        try {
            const r = _rates();
            // Normalised to a 720p-area frame so a later estimate at another
            // resolution scales by pixels — per-DEVICE cost is linear in area
            // (measured locally: exec+toBlob dominate and both scale).
            const fresh = (o.wallMs / o.outSec) / (o.areaF || 1);
            r[o.lane] = r[o.lane] ? Math.round(0.5 * r[o.lane] + 0.5 * fresh) : Math.round(fresh);
            localStorage.setItem(RATE_KEY, JSON.stringify(r));
        } catch (_) { /* private mode etc. — priors keep working */ }
    }
    function measuredSec(lane, outSec, areaF) {
        const r = _rates()[lane];
        return r ? (r * outSec * (areaF || 1)) / 1000 : 0;
    }

    /**
     * Estimate wall-clock seconds for the no-WebCodecs MP4 fallback.
     *
     * Legacy shape (no `lane`): the original MediaRecorder+transcode model.
     * ⚠️ slideshow-maker STOPPED running that pipeline on 2026-07-02 (its
     * no-H.264 MP4 is now WebCodecs VP9 + FFmpeg ultrafast) but kept calling
     * this shape until 2026-08-18 — the dialog then quoted 3–5× the real
     * time ("5–7 min" for a 2:09 export) and a paying user read the title as
     * "cannot download". It now uses lanes 'ssm-vp9' + 'ssm-tx'. Karaoke is
     * the remaining legacy-shape caller — verify before assuming.
     *
     * Lane-aware shape (`lane` given, e.g. audio-to-video's per-frame
     * PNG→libx264 path): a measured rate for this device wins; otherwise
     * population priors from the RU-shard field data (60 d to 2026-08-13,
     * n=133 completed slow-lane exports, actual wall / audio duration):
     *   deviceMemory ≤4 GB   p25–p75 = 4.7–14.6×   (median 9.2)
     *   4–8 GB               p25–p75 = 3.3–8.9×    (median 5.7)
     *   >8 GB                p25–p75 = 2.1–8.1×    (median 3.8)
     * Resolution does NOT enter the prior: field ratios at 720p and 1080p
     * are statistically the same (4.23 vs 4.07 median) — people who pick
     * 1080p sit on faster machines, and the prior reflects outcomes, not
     * per-pixel physics. The old model promised dur×1.3–1.9 on ≥8 GB
     * desktops; the field showed 4–8× — that lie is what kept #1371's
     * reporter on a 40-minute lane five times in a row.
     */
    /* First-run priors, [p25, median, p75] × output duration, by deviceMemory
       bucket. Only the FIRST run on a device reads these — recordActual()
       replaces them with the device's own rate afterwards.
         a2v-ff  — a rendered frame → libx264, in batches (RU field, 60 d to
                   2026-08-13, n=133 — RECALIBRATED 16.09.2026, see below).
         a2v-vp9 — the fast lane's picture rendered as VP9 (WebCodecs software
                   encoder): field median 0.35× duration for a2v-wc (n=2414);
                   VP9 software runs a little behind hardware H.264.
         a2v-tc  — one libx264 ultrafast pass over that WebM in FFmpeg-WASM
                   (measured 2026-09-13 on a 32-core desktop: 0.6× duration at
                   720p; scaled by the same memory buckets as a2v-ff, which is
                   the same encoder on the same class of machines).
       Any other lane key falls back to the PNG lane's numbers — the slowest
       honest answer. */
    const PRIORS = {
        /* Halved on 16.09.2026, because the lane stopped shipping frames to
           FFmpeg as PNGs (it spent more time DECODING those pictures than
           encoding the video) — the numbers above were measured in the PNG era
           and no longer describe what runs.
           The replacement is NOT an A/B: it is the absolute speed of the lane
           as it now ships, measured against production on this desktop (8 GB,
           the `high` bucket) with a 30-second 720p clip and a visualiser —
           25.5 / 53.8 / 77.6 / 91.1 s over four completed exports, i.e.
           0.85–3.0× the duration with a median near 1.9×, which is exactly
           what `high` now says. Machine load was read before and after every
           run and stayed at 2–20 %; a run on a busy machine measures the
           neighbour (the same export took 600 s at 100 %).
           They are cut by 2 rather than by the full measured factor because
           this is only the FIRST guess on a device — recordActual() replaces
           it — and promising less than the lane delivers is the safe side of
           #1371. ⚠️ Field data for the new lane starts on 16.09.2026: until a
           week of `export_complete` has accumulated, these are a calibration
           against one machine, not against the field. */
        'a2v-ff':  { low: [2.4, 4.6, 7.3], mid: [1.7, 2.9, 4.5], high: [1.1, 1.9, 4.1] },
        'a2v-vp9': { low: [0.5, 0.9, 1.6],  mid: [0.3, 0.5, 0.9], high: [0.2, 0.35, 0.6] },
        'a2v-tc':  { low: [1.5, 2.6, 4.2],  mid: [1.0, 1.7, 2.8], high: [0.6, 1.0, 1.8] },
    };
    function firstRunPrior(lane, dm) {
        const row = PRIORS[lane] || PRIORS['a2v-ff'];
        return dm <= 4 ? row.low : dm <= 8 ? row.mid : row.high;
    }

    function estimateSeconds(opts) {
        opts = opts || {};
        const dur = Math.max(1, opts.durationSec || 0);
        const dm = opts.deviceMemory || (navigator && navigator.deviceMemory) || 4;
        if (!opts.lane) {
            const transcodeMult = dm <= 2 ? 6 : dm <= 4 ? 4 : dm <= 8 ? 1.2 : 0.6;
            const lmin = Math.round(dur * (1 + transcodeMult * 0.5));
            const lmax = Math.round(dur * (1 + transcodeMult * 1.5));
            return { minSec: Math.max(30, lmin), maxSec: Math.max(60, lmax), recordSec: Math.round(dur) };
        }
        let minSec, maxSec, midSec, measured = false;
        const pred = measuredSec(opts.lane, dur, opts.areaF);
        if (pred) {
            measured = true;
            minSec = Math.round(pred * 0.8);
            maxSec = Math.round(pred * 1.5);
            midSec = Math.round(pred);
        } else {
            const p = firstRunPrior(opts.lane, dm);
            minSec = Math.round(dur * p[0]);
            midSec = Math.round(dur * p[1]);
            maxSec = Math.round(dur * p[2]);
        }
        // {fast} = the WebM exit's own lane, measured if we have it, else the
        // field median 0.35× duration (fast-lane act/dur, n=2414). The old
        // code printed recordSec/50 — a SECONDS value — as minutes, telling
        // a 6-minute-song user "WebM: 7 minutes" while overselling MP4 8×.
        const fastPred = opts.fastLane ? measuredSec(opts.fastLane, dur, opts.areaF) : 0;
        const fastSec = Math.max(10, Math.round(fastPred || dur * 0.35));
        return {
            minSec: Math.max(30, minSec), maxSec: Math.max(60, maxSec),
            midSec: Math.max(30, midSec), fastSec: fastSec,
            recordSec: Math.round(dur), measured: measured,
        };
    }

    function fmtMin(sec) { return Math.max(1, Math.round(sec / 60)); }
    function escapeHtml(s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
    }

    function show(opts) {
        opts = opts || {};
        const strings = opts.strings || {};
        const est = opts.est || { minSec: 600, maxSec: 1200, recordSec: 60 };
        const allowKeepMp4 = opts.allowKeepMp4 !== false;
        const allowWebm = opts.allowWebm !== false;

        return new Promise(function (resolve) {
            const prevFocus = document.activeElement;
            const backdrop = document.createElement('div');
            backdrop.className = 'cvslow-backdrop';
            backdrop.setAttribute('role', 'dialog');
            backdrop.setAttribute('aria-modal', 'true');
            backdrop.setAttribute('aria-labelledby', 'cvslowTitle');

            const bodyTxt = String(strings.body || '')
                .replace('{min}', String(fmtMin(est.minSec)))
                .replace('{max}', String(fmtMin(est.maxSec)))
                // Lane-aware estimates carry the WebM exit's own prediction
                // (fastSec). Legacy callers keep the old recordSec/50 value —
                // for the realtime MediaRecorder lane it lands near the deck
                // length in minutes, which is roughly right there.
                .replace('{fast}', String(est.fastSec != null ? fmtMin(est.fastSec)
                                                              : Math.max(1, Math.round(est.recordSec / 50))));

            backdrop.innerHTML =
                '<div class="cvslow-modal">' +
                  '<h3 id="cvslowTitle" class="cvslow-title">' + escapeHtml(strings.title) + '</h3>' +
                  '<p class="cvslow-body">' + escapeHtml(bodyTxt) + '</p>' +
                  (strings.hint ? '<p class="cvslow-body cvslow-hint">' + escapeHtml(strings.hint) + '</p>' : '') +
                  '<div class="cvslow-actions">' +
                    (allowWebm
                      ? '<button type="button" class="cvslow-webm">' + escapeHtml(strings.useWebm) + '</button>'
                      : '') +
                    (allowKeepMp4
                      ? '<button type="button" class="cvslow-mp4">' + escapeHtml(strings.keepMp4) + '</button>'
                      : '') +
                    '<button type="button" class="cvslow-cancel">' + escapeHtml(strings.cancel) + '</button>' +
                  '</div>' +
                '</div>';

            function done(choice) {
                try { document.removeEventListener('keydown', onKey, true); } catch (_) {}
                try { backdrop.remove(); } catch (_) {}
                try { if (prevFocus && prevFocus.focus) prevFocus.focus(); } catch (_) {}
                resolve(choice);
            }
            function onKey(e) {
                if (e.key === 'Escape') { e.preventDefault(); done('cancel'); }
            }
            backdrop.addEventListener('click', function (e) {
                if (e.target === backdrop) done('cancel');
            });
            const webmBtn = backdrop.querySelector('.cvslow-webm');
            if (webmBtn) webmBtn.addEventListener('click', function () { done('webm'); });
            const mp4Btn = backdrop.querySelector('.cvslow-mp4');
            if (mp4Btn) mp4Btn.addEventListener('click', function () { done('mp4'); });
            backdrop.querySelector('.cvslow-cancel').addEventListener('click', function () { done('cancel'); });
            document.addEventListener('keydown', onKey, true);

            document.body.appendChild(backdrop);
            try { (backdrop.querySelector('.cvslow-webm')
                   || backdrop.querySelector('.cvslow-mp4')
                   || backdrop.querySelector('.cvslow-cancel')).focus(); } catch (_) {}
        });
    }

    window.CanvasVideoSlowPath = {
        estimateSeconds: estimateSeconds,
        recordActual: recordActual,
        show: show,
    };
})();
